const yyyyMMddDashed = 'YYYY-MM-DD';
import { Plans } from './plans';
import { Payment, Order } from 'types';
import { UI } from './ui';
import { validateEmail, validateName, Crypto } from '@/utils';
import { setThemeColorByCategory } from '@/settings/theme-color';
import '@/extensions/array';
import { Currency } from './settings';
import i18n from '@/plugins/i18n';

export namespace User {
  export interface Session {
    session_id: string;
  }

  export interface LoyaltyProgrammsResponse {
    hotels: any;
  }

  export interface Programms {
    list: Programm[];
  }

  export interface Programm {
    id: string;
    title: string;
  }

  export class Credentials {
    session_id: string;
    user_id: string;
    subscription_id?: string;
    is_signup?: boolean;

    constructor(session_id: string, user_id: string, subscription_id: string) {
      this.session_id = session_id;
      this.user_id = user_id;
      this.subscription_id = subscription_id;
    }
  }

  export interface AwardWalletUrl {
    url: string;
  }

  export interface UserRepsonse {
    user: User;
    cover_stats: Stats;
    pending_3ds_orders?: Order.UsersPendingOrders[];
  }

  export interface Stats {
    savings: Savings;
  }

  export interface Savings {
    total: number;
  }

  export interface SavingsOffset {
    amount: number;
    priceLimit: number;
    currency: string;
  }

  export interface User {
    user_id: string;
    bin?: string;
    bank_name?: string;
    email: string;
    cell_number: string;
    country: string;
    default_card_id: string;
    social_login: Social[];
    subscriptions: Subscription[];
    payment_cards: Card[];
    first_name: string;
    last_name: string;
    registered_by: RegisteredBy;
    user_groups: string[];
    register_params?: RegisterParams;
    created: Date;
    created_at: Date;
  }

  export interface RegisterParams {
    opt_bin8?: string;
    bin_country?: string;
    customer_bank?: string;
    customer_id?: string;
    bin_preferences?: {
      test?: boolean;
      do_not_check_bin?: boolean;
      individual?: boolean;
      full_pan_required?: boolean;
    };
  }

  export const getSubscription = (user: User): Subscription => {
    if (!user) return null;
    const subId = localStorage.getItem('x-subscription-id');
    return localStorage.getItem('view') && subId
      ? getSubscriptionById(user, subId)
      : getOwnSubscription(user)
      ? getOwnSubscription(user)
      : user.subscriptions.firstOrNull();
  };

  export const getOwnSubscription = (user: User): Subscription => {
    if (!user) return null;
    return user.subscriptions.find(it => it.role === 'owner');
  };

  export const getSubscriptionById = (
    user: User,
    subId: string,
  ): Subscription => {
    if (!user || !subId) return null;
    return user.subscriptions.find(it => it.id === subId);
  };

  export interface RegisteredBy {
    skipped: boolean;
    code?: any;
  }

  export interface Social {
    first_name: string;
    last_name: string;
    user_id: string;
  }

  export interface Subscription {
    id: string;
    subscription_name: string;
    active: boolean;
    request_cvv: boolean;
    is_default: boolean;
    is_paid: boolean;
    travellers: Traveller[];
    savings: number;
    hotels: Hotels;
    credits: Credits;
    plan_id: string;
    next_order_fee?: number;
    role: string;
    free_slots: number;
  }

  export interface Credits {
    credits: number;
    pending: number;
  }

  export interface Hotels {
    book_enabled: boolean;
    free_saving_order_min: number;
    free_stay: boolean;
    free_stay_saving_max: number;
    search_enabled: boolean;
  }

  export interface Traveller {
    traveller_id: string;
    active: boolean;
    age_type: string;
    dob: string;
    first_name: string;
    last_name: string;
    hidden: boolean;
    is_owner: boolean;
    preference: Preference;
    passport: any;
  }

  export interface Preference {
    hotel: HotelPreference;
    cars: any;
    avia: any;
  }

  export interface HotelPreference {
    loyalty_cards: any;
  }

  export interface AddTravellerResponse {
    error_message: string;
    warning_message: string;
    traveller: Traveller;
    status: string;
    message: string;
  }

  export interface Card {
    card_id: string;
    card_mask: string;
    card_type: string;
    exp: string;
    exp_month: string;
    exp_year: string;
    temporary?: Payment.Card;
  }

  export class LoyaltyViewModel {
    programm: Programm;
    number: string;
    isOpened: boolean;
    error: string;

    constructor() {
      this.programm = undefined;
      this.number = '';
      this.isOpened = false;
      this.error = '';
    }
  }

  export class NewTraveller {
    isSubmitted: boolean = false;
    error: string = '';
    firstName: string = '';
    lastName: string = '';
    isModalOpen: boolean = false;

    constructor() {}

    get fullName() {
      return `${this.firstName} ${this.lastName}`;
    }

    isFistNameValid(): string {
      if (!this.firstName) return String(i18n.global.t('errors.field.empty'));
      return '';
    }

    isLastNameValid(): string {
      if (!this.lastName) return String(i18n.global.t('errors.field.empty'));
      return '';
    }

    createRequestBody(): object {
      const body = {
        first_name: this.firstName,
        last_name: this.lastName,
      };
      return body;
    }

    isValid(): boolean {
      if (this.isFistNameValid() !== '') return false;
      if (this.isLastNameValid() !== '') return false;
      return true;
    }
  }

  export interface SocialCredentials {
    token: string;
    source: string;
  }

  export class Contacts {
    isSubmitted: boolean = false;
    phone: string = '';
    email: string = '';
    phoneIsValid: boolean = true;
    created: boolean = false;
    error: string = '';
    flightNumber: string = '';

    constructor() {}

    isPhoneValid(): string {
      if (!this.phone) return String(i18n.global.t('errors.field.empty'));
      if (!this.phoneIsValid)
        return String(i18n.global.t('errors.field.invalidPhone'));
      return '';
    }

    isEmailValid(): string {
      return validateEmail(this.email);
    }

    isValid(): boolean {
      if (this.isEmailValid() !== '') return false;
      if (this.isPhoneValid() !== '') return false;
      return true;
    }
  }

  export class Guest {
    isSubmitted: boolean = false;
    created: boolean = false;
    traveller: string = null;
    error: string = '';

    constructor() {}

    isTravelerSelected(): string {
      if (!this.traveller) return String(i18n.global.t('errors.field.empty'));
      return '';
    }

    isValid(): boolean {
      if (!this.traveller) return false;
      return true;
    }
  }

  export interface SelectedPlan {
    plan: string;
    planModel: Plans.Plans;
  }

  export interface CurrentPlan {
    planId: string;
    planName: string;
    isPaid: boolean;
  }

  export class Split {
    enable: boolean = true;
    plan: string = '';
    planModel: Plans.Plans = null;
    screens: Plans.Screens = null;
    feeFromUser: number = 0;
    currentPlan: CurrentPlan = null;

    constructor() {}

    splitSelected(): boolean {
      if (!this.screens) return true;
      if (!this.showPlans()) return true;
      return this.planModel ? true : false;
    }

    getSplit(): number {
      if (!this.screens) return SplitType.Nothing;
      return SplitType.Nothing;
    }

    showPlans(): boolean {
      if (this.currentPlanIsOld()) return false;
      return !this.isCurrentPlanPaid();
    }

    currentPlanIsOld(): boolean {
      if (!this.currentPlan) return true;
      if (!this.screens) return true;
      if (
        this.screens.SUBS_OPTIONS.plans.findIndex(
          it => it.id.substr(0, 4) === this.currentPlan.planId.substr(0, 4),
        ) < 0
      )
        return true;
      else return false;
    }

    isCurrent() {
      if (!this.planModel) return false;
      if (!this.currentPlan) return false;
      if (this.currentIsPAYG()) return false;
      else return this.currentPlan.planId === this.planModel.id;
    }

    getTextOnSelect(): string {
      if (!this.planModel) return '';
      if (this.isCurrent())
        return `Your current plan is ${this.planModel.title_text} Subscription`;
      return this.planModel.if_selected;
    }

    getPlanTypeOnSkip(): string {
      switch (this.getSplit()) {
        case 2:
          return 'split_saving';
        default:
          return 'free';
      }
    }

    currentIsPAYG(): boolean {
      if (!this.currentPlan) return false;
      return (
        this.currentPlan.planId === 'ba5e0000-2cc7-4838-a135-d0f9d347fbf7' ||
        this.currentPlan.planId === 'ba5e0100-8331-4c67-b5fc-14a384a7894b'
      );
    }

    selectedIsPAYG(): boolean {
      if (!this.planModel) return false;
      return (
        this.planModel.id === 'ba5e0000-2cc7-4838-a135-d0f9d347fbf7' ||
        this.planModel.id === 'ba5e0100-8331-4c67-b5fc-14a384a7894b'
      );
    }

    isCurrentPlanPaid(): boolean {
      if (!this.currentPlan) return false;
      if (this.currentIsPAYG()) return false;
      return this.currentPlan.isPaid;
    }

    selectedAndCurrentIsSame(): boolean {
      if (this.currentPlanIsOld()) return false;
      if (!this.planModel || !this.currentPlan) return false;
      return this.planModel.id === this.currentPlan.planId;
    }

    getOrderFee() {
      if (this.isCurrent()) return this.feeFromUser ? this.feeFromUser : 0;
      else
        return this.planModel && this.planModel.next_order_fee
          ? this.planModel.next_order_fee
          : 0;
    }

    getPlanByType(): Plans.Screen {
      if (this.screens) {
        return this.screens.SUBS_OPTIONS;
      } else return null;
    }

    getOriginalPlanPrice(): number {
      if (!this.planModel) return 0;
      if (!this.currentPlan) return 0;
      if (this.currentPlanIsOld()) return 0;
      if (this.selectedIsPAYG()) return 0;
      if (this.selectedAndCurrentIsSame() && this.isCurrentPlanPaid()) return 0;
      else return this.planModel.subscription_fee;
    }

    getPlanPriceFormatted(): string {
      if (!this.planModel) return '';
      if (this.currentPlanIsOld()) return '';
      if (this.selectedAndCurrentIsSame() && this.isCurrentPlanPaid())
        return '';
      else
        return String(
          i18n.global.t('offers.planTitleText', {
            title: this.planModel.title_text,
            fee: this.planModel.subscription_fee.formatCurrency({
              round: true,
            }),
            period:
              this.planModel.subscription_period_month === 12
                ? i18n.global.t('offers.year')
                : i18n.global.t('offers.month'),
          }),
        );
    }

    selectedPlanLabel(): string {
      if (this.currentPlanIsOld()) return '';
      if (!this.planModel) return '';
      return String(i18n.global.t('offers.subscriptionPlan'));
    }

    reset() {
      this.enable = true;
      this.plan = '';
      this.planModel = null;
      this.currentPlan = null;
      this.feeFromUser = 0;
    }
  }

  enum SplitType {
    Nothing = 0,
    Full = 1,
    First = 2,
  }

  export class UserCreditsState {
    credits: UserCredits = new UserCredits();
    isLoading: boolean = false;
    error: string = '';

    constructor() {}
  }

  export interface UserCreditsResponse {
    credits: UserCredits;
    status: string;
  }

  export class UserCredits {
    balance: Balance = null;
    credits: CreditsItem[] = [];
    payout: Payout = null;
    pending: CreditsItem[] = [];

    constructor() {}
  }

  export interface Balance {
    credits: number;
    currency: Currency.CURRENCIES;
    pending: number;
  }

  export interface Credits {
    credits: number;
    currency: Currency.CURRENCIES;
    pending: number;
  }

  export interface Payout {
    amount: number;
    currency: Currency.CURRENCIES;
    is_payout_avail: boolean;
    payout_text: string;
  }

  export interface CreditsItem {
    text: string;
    amount: number;
    currency: Currency.CURRENCIES;
    movement_type: CREDITS_MOVEMENT_TYPE;
    status: CREDITS_STATUS;
    order_link: string;
    subtext: string;
    completed: string;
  }

  export enum CREDITS_MOVEMENT_TYPE {
    IN = 'IN',
    OUT = 'OUT',
  }

  export enum CREDITS_STATUS {
    OK = 'OK',
    CANCEL = 'CANCEL',
  }

  export interface UpcomingTripsResponse {
    upcoming: UpcomingTrip[];
    active?: UpcomingTrip[];
    past?: UpcomingTrip[];
    experience: any;
  }

  export interface UpcomingTrip {
    order_id: string;
    order_number: string;
    show_info: boolean;
    manage_booking: ManageBooking[];
    image_url: string;
    status: string;
    type: TRIP_TYPE;
    partner?: string;
    segments: SegmentItem[];
    travelers: Traveler[];
    start_time: Date;
    thrred_url?: string;
  }

  export enum TRIP_TYPE {
    HOTEL = 'hotel',
    FLIGHT = 'flight',
    CAR = 'car',
  }

  export interface ManageBooking {
    action: MANAGE_BOOKING_ACTION;
    action_name: string;
    available: boolean;
  }

  export enum MANAGE_BOOKING_ACTION {
    CANCEL = 'cancel',
    MAIL_RECEIPT = 'mailReceipt',
  }

  export interface SegmentItem {
    stars?: number;
    hotel?: string;
    id?: string;
    name?: string;
    sl_index?: number;
    class?: string;
    flight?: string;
    flightN?: string;
    airline?: string;
    check_in?: string;
    vendor?: string;
    type?: string;
    make_model?: string;
    location?: string;
    start_date?: Date;
    end_date?: Date;
    from: SegmentFromTo;
    to: SegmentFromTo;
  }

  export interface SegmentFromTo {
    date: string;
    time: string;
    city?: string;
    airport?: string;
    terminal?: string;
  }

  export interface Traveler {
    room_type?: string;
    type?: string;
    class?: string;
    name: string;
    number?: string;
    status: TRAVELER_STATUS;
    confirmation: string;
  }

  export enum TRAVELER_STATUS {
    PROCESSING = 'processing',
    CONFIRMED = 'confirmed',
    BOOKED = 'booked',
    ISSUED = 'issued',
  }

  export interface _Country {
    code: string;
    iso2: string;
    iso3: string;
    name: string;
    phone_length: string;
  }

  export interface CountriesResponse {
    countries: _Country[];
  }

  export interface SpecialBin {
    message: string;
    key: string;
    partner: string;
    status: string;
    fullPanRequired: boolean;
  }

  export class CheckBinForm extends UI.Form {
    bin: UI.FormField = new UI.FormField();
    isBinChecked: boolean = false;
    isBinValid: boolean = false;
    isBinFocused: boolean = false;

    binFormatted(): string {
      return `${this.bin.value.substring(0, 4)} ${this.bin.value.substring(4)}`;
    }

    isBinEmpty() {
      return this.bin.value.trim().length === 0;
    }

    isBinFilled() {
      return this.bin.value.trim().length === 8;
    }

    isMastercard() {
      return (
        this.bin.value?.trim()?.length === 0 ||
        this.bin.value?.trim()?.startsWith('5')
      );
    }

    resetError() {
      this.bin.resetError();
      this.isBinChecked = false;
      this.isBinValid = false;
    }

    reset() {
      this.bin.reset();
      this.isBinChecked = false;
      this.isBinValid = false;
      this.isBinFocused = false;
    }
  }

  export class SignInForm extends UI.Form {
    email: UI.FormField = new UI.FormField('', validateEmail);

    isValid(): boolean {
      this.email.resetError();
      this.email.validate();

      return this.email.error.length === 0;
    }

    createRequestBody(): ML.Request.Login {
      return {
        email: this.email.value.trim(),
        isLogin: true,
      };
    }
  }

  export class SignUpForm extends UI.Form {
    email: UI.FormField = new UI.FormField('', validateEmail);
    firstName: UI.FormField = new UI.FormField('', validateName);
    lastName: UI.FormField = new UI.FormField('', validateName);
    cardNumberLastDigits: UI.FormField = new UI.FormField('');
    isCardNumberLastDigitsValid: boolean = false;
    isFilled: boolean = false;
    specialBinPayload: SpecialBin = null;

    fullName(): string {
      let arr = [];
      if (this.firstName.value) arr.push(this.firstName.value);
      if (this.lastName.value) arr.push(this.lastName.value);
      return arr.join(' ');
    }

    isFullCardNumberIsValid(bin: string): boolean {
      this.cardNumberLastDigits.error = '';

      if (!this.cardNumberLastDigits.value) {
        this.isCardNumberLastDigitsValid = false;
        return false;
      }

      const cardNumber = (bin + this.cardNumberLastDigits.value)
        .split(' ')
        .join('');

      if (cardNumber.length < 16) {
        this.isCardNumberLastDigitsValid = false;
        return false;
      }

      const luhn = Payment.Card.checkLuhn(cardNumber);

      const zeroPattern = /^[0-9]{6}0000000000$/;

      const zeroMatched = zeroPattern.test(cardNumber);

      if (!luhn || zeroMatched) {
        this.isCardNumberLastDigitsValid = false;
        this.cardNumberLastDigits.error = i18n.global.t(
          'errors.field.invalidCardNumber',
        );
        return false;
      }

      this.isCardNumberLastDigitsValid = true;
      return true;
    }

    fullCardNumberRequired() {
      return (
        this.specialBinPayload !== null &&
        this.specialBinPayload.fullPanRequired
      );
    }

    isPayoneer(): boolean {
      if (!this.specialBinPayload) {
        return false;
      }

      return this.specialBinPayload?.partner
        ?.toLowerCase()
        ?.includes('payoneer');
    }

    isValid(): boolean {
      this.email.resetError();
      this.firstName.resetError();
      this.lastName.resetError();

      this.email.validate();
      this.firstName.validate();
      this.lastName.validate();

      return (
        this.email.error.length === 0 &&
        this.firstName.error.length === 0 &&
        this.lastName.error.length === 0
      );
    }

    createRequestBody(bin?: string): ML.Request.Register {
      const body: ML.Request.Register = {
        email: this.email.value.trim(),
        name: this.firstName.value.trim() + ' ' + this.lastName.value.trim(),
      };

      if (bin) {
        body.bin = bin;
      }

      if (this.fullCardNumberRequired()) {
        const fullNumber = (bin + this.cardNumberLastDigits.value)
          .split(' ')
          .join('');

        const maskCreditCard = (cardNumber: string): string => {
          if (cardNumber.length !== 16) {
            return cardNumber;
          }
          return `${cardNumber.slice(0, 8)}XXXX${cardNumber.slice(12)}`;
        };

        const masked = maskCreditCard(fullNumber);

        const encoded =
          this.specialBinPayload.key && this.specialBinPayload.key.length > 0
            ? Crypto.encryptText(this.specialBinPayload.key, masked)
            : masked;

        body.binEncoded = encoded;
      }

      return body;
    }
  }

  export class SignUpConsentForm extends UI.Form {
    isAdult: UI.CheckboxField = new UI.CheckboxField(false, true);
    isAcceptTransferPersonalData: UI.CheckboxField = new UI.CheckboxField(
      false,
      true,
    );
    isAcceptSharingPersonalData: UI.CheckboxField = new UI.CheckboxField(
      false,
      true,
    );

    isValid(): boolean {
      this.isAdult.isValidationEnabled = true;
      this.isAcceptTransferPersonalData.isValidationEnabled = true;
      this.isAcceptSharingPersonalData.isValidationEnabled = true;

      return (
        this.isAdult.isValid() &&
        this.isAcceptTransferPersonalData.isValid() &&
        this.isAcceptSharingPersonalData.isValid()
      );
    }
  }

  export enum OrderTimelineTag {
    Future = 'future',
    Now = 'now',
    Past = 'past',
  }

  export interface TripsHistoyItem {
    order_id: Order.OrderItem['order_id'];
    order_status: Order.OrderItemStatus;
    order_number: Order.OrderItem['order_number'];
    order_type: Order.OrderProduct;
    order_source: string;
    created_at: Date;
    changed_at: Date;
    order_timeline_tag: OrderTimelineTag;
    start_date: string;
    end_date: string;
    supplier_code: string;
    supplier_confirmation_id: string;
    product_name: string;
    product_location: string;
    product_rating: number;
    product_description: string;
    product_image_url: string;
    product_url?: string;
    traveler_name: string;
    cashback_amount?: number;
    cashback_currency?: string;
  }

  export namespace PersistenceGuestData {
    const KEY = 'guest-data';

    export interface Payload {
      userId?: string;
      phone?: string;
      traveler?: string;
      arrivalTime?: string;
      specialRequest?: string;
    }

    function getUserId(): string | undefined {
      try {
        const auth = localStorage.getItem('auth');

        const parsedAuth = JSON.parse(auth);
        const userId = localStorage.getItem('x-user-id');

        return parsedAuth?.userId || userId;
      } catch (e) {
        return undefined;
      }
    }

    export function save(data: Payload) {
      const userId = getUserId();

      if (!userId) {
        clear();
        return;
      }

      const existing = get();

      if (existing?.userId !== userId) {
        clear();
      }

      const merged = { ...existing, ...data, userId };

      localStorage.setItem(KEY, JSON.stringify(merged));
    }

    export function get(): Payload | null {
      try {
        const stored = JSON.parse(localStorage.getItem(KEY)) as Payload;
        const userId = getUserId();

        if (stored && stored.userId === userId) {
          return stored;
        }

        clear();

        return null;
      } catch (e) {
        return null;
      }
    }

    export function clear() {
      localStorage.removeItem(KEY);
    }
  }

  export namespace UserCategory {
    const KEY = 'x-user-category';
    const PricingModeKey = 'x-pricing-mode';

    export const nonBusinessDefaultBin = '11112222';

    export enum Category {
      Business = 'business',
      Individual = 'individual',
      Personal = 'personal', // deprecated, need to merge to Individual
    }

    export function get(): Category {
      const stored = localStorage.getItem(KEY);

      if (stored === Category.Personal) {
        setThemeColorByCategory(Category.Individual);
        return Category.Individual;
      }

      if (stored && Object.values(Category).includes(stored as Category)) {
        setThemeColorByCategory(stored as Category);
        return stored as Category;
      }

      return Category.Business;
    }

    export function set(value: Category) {
      if (value === Category.Personal) {
        localStorage.setItem(KEY, Category.Individual);
        setThemeColorByCategory(Category.Individual);
        return;
      }

      localStorage.setItem(KEY, value);
      setThemeColorByCategory(value);
    }

    export function isSwitcherShown(): boolean {
      const stored = localStorage.getItem(PricingModeKey);

      if (stored && Object.values(Category).includes(stored as Category)) {
        return (
          (stored as Category) === Category.Personal ||
          (stored as Category) === Category.Individual
        );
      }

      return false;
    }

    export function savePricingMode(value: Category) {
      localStorage.setItem(PricingModeKey, value);
    }

    export function clearPricingMode() {
      localStorage.removeItem(PricingModeKey);
    }

    // function setThemeColorByCategory(value: Category) {
    //   document.body.classList.add(`user-category--${value}`);
    //   document
    //     .querySelector('meta[name="theme-color"]')
    //     ?.setAttribute(
    //       'content',
    //       value === Category.Individual ? '#5e06b6' : '#141413',
    //     );
    // }
  }

  export interface UserV2 {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    type: UserCategory.Category;
    bin: string;
    country: string;
    language: string;
    currency: Currency.CURRENCIES;
    isPaid: boolean;
    creditsUsed: boolean;
    annaMoneyUser: boolean;
    travelers: string[];
    wallets: UserWallet[];
    walletsEUR: UserWallet[];
    credits: {
      active: number;
      pending: number;
      currency: Currency.CURRENCIES;
      history: UserV2Credits.CreditsItem[];
    };
    payout: {
      currency: Currency.CURRENCIES;
      available: boolean;
      availableAmount: number;
      requiredAmount: number;
      b2p: boolean;
      internalBankTransfer: boolean;
      ibanBankTransfer?: boolean;
      useOnHotels: boolean;
      redeemAsRefund: boolean;
      b2b: boolean;
    };
    orders: UserOrderPreview.Order[];
    address: UserAddress;
  }

  export interface UserWallet {
    multi: boolean;
    currency: Currency.CURRENCIES | string;
    active: number;
    pending: number;
  }

  export interface UserAddress {
    address?: string;
    city?: string;
    countryCode?: string;
    stateCode?: string;
    postCode?: string;
  }

  // TODO: replace to orders after merge to FW-289
  export namespace UserV2Credits {
    export interface CreditsItem {
      id: number;
      credits?: number;
      currency?: Currency.CURRENCIES | string;
      type?: CreditsTypes;
      comment?: string;
      source?: CreditsSources;
      category?: CreditsCategories;
      created?: string;
      checkin?: string;
      checkout?: string;
      complete?: string;
      amount?: number;
      hotelName?: string;
    }

    export enum CreditsTypes {
      Active = 'active',
      Pending = 'pending',
      Revoked = 'revoked',
      Payout = 'payout',
      AnnaMoneyPayout = 'anna_money_payout',
      Canceled = 'canceled',
    }

    export enum CreditsSources {
      Empty = '',
      Hotel = 'hotel',
    }

    export enum CreditsCategories {
      Manual = 'manual',
      Cashback = 'cashback',
      Credits = 'credits',
    }
  }

  export namespace UserOrderPreview {
    export interface Order {
      _id?: number;
      id: string;
      externalId?: string;
      status?: Order.Ml.OrderStatus;
      type?: Order.Ml.OrderTypes;
      provider?: string;
      created?: string;
      checkin?: string;
      checkout?: string;
      complete?: string;
      userName?: string;
      travelers?: string[];
      hotelId?: number;
      hotelLabel?: string;
      hotelName?: string;
      hotelAddress?: string;
      hotelThumb?: string;
      hotelStars?: number;
      adults?: number;
      children?: number[];
      label?: string;
      room?: string;
      comment?: string;
      amount?: number;
      currency?: Currency.CURRENCIES | string;
      cashback?: number;
      save?: number;
      orderUrl?: string;
      orderType?: Order.Ml.OrderProductType;
    }
  }

  export namespace ML {
    export namespace Request {
      export interface PayoutBody {
        amount: number;
        currency?: Currency.CURRENCIES;
        cardId?: string;
        cvc?: string;
        cardNumber?: string;
        cardHolder?: string;
        cardYear?: string;
        cardMonth?: string;
        cardCVV?: string;
      }

      export interface UserDataBody {
        name?: string;
        phone?: string;
        bin?: string;
        address?: UserAddress;
      }

      export interface Login {
        email?: string;
        password?: string;
        token?: string;
        isLogin?: boolean;
      }

      export interface Register {
        email?: string;
        name?: string;
        phone?: string;
        bin?: string;
        binEncoded?: string;
        password?: string;
        isLogin?: boolean;
      }
    }

    export namespace Response {
      export interface Payout {
        amount: number;
        currency: Currency.CURRENCIES;
        status: string;
      }
      export interface Auth {
        fullPanRequired?: boolean;
        name?: string;
        email?: string;
        type?: UserCategory.Category;
        partnerId?: number;
        partner?: string;
        userId?: string;
        sessionId: string;
        isAdmin?: boolean;
        message?: string;
        key?: string;
        status?: string;
        legacy?: boolean;
      }
    }
  }
}
